/* Rack picker — the put-away control.
 *
 * A searchable dropdown over a store's racks that can also CREATE the rack the
 * operator typed. That last part is the whole point: goods inward happens at the
 * shelf, and a receipt must not be blocked because a rack number exists on the
 * floor but not yet in the master. Type "R-05", the list comes up empty, and the
 * footer offers to create it — one gesture, no trip to the rack master.
 *
 * Built rather than reusing supplyKit's SearchableSelect because that control has
 * no way to act on a query that matches nothing, which is exactly the case this
 * has to handle.
 *
 * The menu is portalled to <body> and positioned fixed, like the GRN's medicine
 * typeahead: every table this sits in (supplyKit's Table, the GRN line table) is
 * an overflow:auto scroller, and an absolutely-positioned menu inside one is
 * clipped at the panel edge.
 *
 * `value` is a rackId (number) or null — a line with no rack is received
 * unracked, which is allowed. `onChange(rackId | null, rack | null)`.
 * `onCreate(code)` must resolve to the created rack; the picker then selects it.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Plus, Search } from "lucide-react";
import { rackCodeError } from "./rackService";
import RackCreateDialog from "./RackCreateDialog";

const MENU_MIN_WIDTH = 230;
const MENU_MAX_HEIGHT = 300;

export const RackPicker = ({
  value,
  racks = [],
  onChange,
  onCreate,
  disabled = false,
  disabledHint = "",
  fallbackLabel = "",
  placeholder = "—",
  width = 120,
  height = 24,
  align = "left",
  /* Overrides the hover text. Worth setting where the value needs explaining — a
     rack shown because it is the medicine's home shelf rather than this batch's
     recorded location, say. */
  title,
  /* Muted rendering for a value that is a hint rather than a fact. */
  tone = "default",
  /* Named in the create dialog's subtitle, so it is obvious which store the new
     rack is being filed under. */
  warehouseLabel = "",
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  /* Code being created — the details dialog is open while this is set. */
  const [creatingCode, setCreatingCode] = useState(null);
  const [err, setErr] = useState(null);
  /* Viewport rect of the button — the portalled menu is positioned against it. */
  const [anchor, setAnchor] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const selected = racks.find((r) => String(r.id) === String(value));
  /* A rack that is not in the loaded list — a deactivated one carried on an
     older GRN line — still has to render as something other than blank. */
  const label = selected ? selected.code : value != null && fallbackLabel ? fallbackLabel : "";

  /* Close on a click that lands outside BOTH the button and the portalled menu —
     the menu is not a DOM descendant of the button, so one ref is not enough. */
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  /* Reopening starts clean — a stale query and a stale error both mislead. */
  useEffect(() => {
    if (!open) {
      setQuery("");
      setErr(null);
      setAnchor(null);
    }
  }, [open]);

  /* Keep the menu glued to the button while anything scrolls or resizes. `true`
     on the scroll listener catches the table scrollers and drawer bodies, which
     don't bubble scroll events. */
  useLayoutEffect(() => {
    if (!open) return undefined;
    const measure = () => {
      const el = btnRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const menuWidth = Math.max(MENU_MIN_WIDTH, r.width);
      /* Prefer dropping below and left-aligned; flip up when the viewport bottom
         is closer than the menu is tall, and pull left when it would run off the
         right edge. A picker in the last column of a wide table hits both. */
      const spaceBelow = window.innerHeight - r.bottom;
      const dropUp = spaceBelow < 200 && r.top > spaceBelow;
      const left = Math.max(8, Math.min(r.left, window.innerWidth - menuWidth - 8));
      setAnchor({
        left,
        width: menuWidth,
        ...(dropUp ? { bottom: window.innerHeight - r.top + 2 } : { top: r.bottom + 2 }),
        maxHeight: Math.max(160, Math.min(MENU_MAX_HEIGHT, (dropUp ? r.top : spaceBelow) - 12)),
      });
    };
    measure();
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? racks.filter((r) => `${r.code} ${r.name || ""} ${r.description || ""}`.toLowerCase().includes(q))
    : racks;
  /* Only offer to create when nothing already answers to that code — matching
     the backend's unique (warehouse, code) index, which is case-insensitive. */
  const exact = racks.find((r) => r.code.toLowerCase() === q);
  const canOfferCreate = Boolean(onCreate) && q.length > 0 && !exact;

  const choose = (rack) => {
    onChange(rack ? rack.id : null, rack || null);
    setOpen(false);
  };

  /* Don't create on the spot — hand the typed code to the details dialog, so the
     rack gets a name while the person who knows what is on the shelf is still
     looking at it. The menu closes; the dialog carries on above it. */
  const handleCreate = () => {
    const typed = query.trim();
    const problem = rackCodeError(typed);
    if (problem) {
      setErr(problem);
      return;
    }
    setErr(null);
    setCreatingCode(typed);
    setOpen(false);
  };

  const menu = (
    <div
      ref={menuRef}
      className="cb-animate-in"
      style={{
        position: "fixed",
        left: anchor?.left,
        top: anchor?.top,
        bottom: anchor?.bottom,
        width: anchor?.width,
        zIndex: 95,
        background: "var(--bg-panel)",
        border: "1px solid var(--border-default)",
        borderRadius: 6,
        boxShadow: "0 8px 24px rgba(0,0,0,0.16)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ padding: 6, borderBottom: "1px solid var(--border-default)" }}>
        <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
          <Search size={13} strokeWidth={1.6} style={{ position: "absolute", left: 8, color: "var(--fg-muted)" }} />
          <input
            autoFocus
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setErr(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setOpen(false);
                return;
              }
              /* Enter takes the single match, or creates what was typed — the
                 operator's hands are on the keyboard and the goods. */
              if (e.key !== "Enter") return;
              e.preventDefault();
              if (filtered.length === 1) choose(filtered[0]);
              else if (canOfferCreate) handleCreate();
            }}
            placeholder="Rack code or name…"
            style={{
              width: "100%",
              height: 26,
              padding: "0 8px 0 26px",
              border: "1px solid var(--border-default)",
              borderRadius: 4,
              fontSize: 12,
              fontFamily: "var(--font-sans)",
              background: "var(--bg-panel)",
              color: "var(--fg-default)",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, maxHeight: anchor?.maxHeight, overflowY: "auto", padding: 4 }}>
        {value != null && (
          <button type="button" onClick={() => choose(null)} style={optionStyle(false)}>
            <span style={{ color: "var(--fg-muted)" }}>— No rack —</span>
          </button>
        )}
        {filtered.length === 0 && !canOfferCreate && (
          <div style={{ padding: "8px 10px", fontSize: 12, color: "var(--fg-muted)" }}>
            {racks.length === 0 ? "No racks in this store yet." : "No matches"}
          </div>
        )}
        {filtered.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => choose(r)}
            style={optionStyle(String(r.id) === String(value))}
          >
            <span style={{ fontFamily: "var(--font-mono)", fontWeight: 600 }}>{r.code}</span>
            {r.name && r.name !== r.code && (
              <span style={{ marginLeft: 6, color: "var(--fg-muted)" }}>{r.name}</span>
            )}
          </button>
        ))}
      </div>

      {canOfferCreate && (
        <div style={{ borderTop: "1px solid var(--border-default)", padding: 6 }}>
          <button
            type="button"
            onClick={handleCreate}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              width: "100%",
              padding: "6px 8px",
              border: 0,
              borderRadius: 4,
              background: "var(--ink-50)",
              color: "var(--ink-700)",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <Plus size={13} strokeWidth={2} />
            {`Create rack “${query.trim().toUpperCase()}”…`}
          </button>
          {err && <div style={{ fontSize: 10, color: "var(--red-700)", marginTop: 4, padding: "0 8px" }}>{err}</div>}
        </div>
      )}
    </div>
  );

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        title={disabled ? disabledHint : title || selected?.name || label || "Storage rack / shelf"}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 4,
          width,
          height,
          padding: "0 6px",
          border: "1px solid var(--border-default)",
          borderRadius: 3,
          background: disabled ? "var(--bg-sunken)" : "var(--bg-panel)",
          color: !label || tone === "muted" ? "var(--fg-muted)" : "var(--fg-default)",
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          fontStyle: tone === "muted" ? "italic" : "normal",
          cursor: disabled ? "not-allowed" : "pointer",
          overflow: "hidden",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: align }}>
          {label || placeholder}
        </span>
        <ChevronDown size={12} strokeWidth={1.6} style={{ flexShrink: 0 }} />
      </button>
      {open && anchor && createPortal(menu, document.body)}
      {creatingCode !== null && (
        <RackCreateDialog
          initialCode={creatingCode}
          warehouseLabel={warehouseLabel}
          onCreate={onCreate}
          /* Created racks are selected straight away — the operator asked for this
             rack because it is the one they are putting stock in. */
          onCreated={(rack) => {
            setCreatingCode(null);
            choose(rack);
          }}
          onCancel={() => setCreatingCode(null)}
        />
      )}
    </>
  );
};

const optionStyle = (active) => ({
  display: "block",
  width: "100%",
  padding: "6px 8px",
  border: 0,
  borderRadius: 4,
  background: active ? "var(--ink-50)" : "transparent",
  color: "var(--fg-default)",
  fontSize: 12,
  textAlign: "left",
  cursor: "pointer",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

export default RackPicker;
